import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { API } from '../constants';
import { PbDispatch } from './index';
import { PbRequest } from './pbRequest';

export interface UsageReportDataPoint {
    contract_closed_at_date: string;
    contract_closed_at_time: string;
    contract_closed_at_timestamp: number;
    reservation_type: string;
    appliance_name: string;
    appliance_long_name: string;
    laundry_room_external_id: string;
    laundry_room_name: string;
    price: number;
    currency: string;
    booking_type: string;
    user_external_id: string;
}

export interface UsageReport {
    contract_period: string;
    month: number;
    year: number;
    data: UsageReportDataPoint[];
}

enum UsageSubscriptionType {
    NONE = 'NONE',
    MONTHLY = 'MONTHLY',
    CUMULATIVE_DAILY = 'CUMULATIVE_DAILY',
}

interface UsageDataSubscriptionDto {
    subscription: UsageSubscriptionType;
    email: string;
}

export interface UsagePeriod {
    period: number;
    month: number;
    year: number;
    cycles: number;
    amount: number;
    currency: string;
}

interface UsagePeriodsDto {
    available_periods: UsagePeriod[];
}

export interface UsageByYearMonth {
    [year: number]: {
        [month: number]: UsagePeriod;
    };
}

interface UsageDataStore {
    usageDataSubscription: UsageDataSubscriptionDto | undefined;
    usageReport: UsageReport | undefined;
    usageByYearMonth: UsageByYearMonth;
}

const initialState: UsageDataStore = {
    usageDataSubscription: undefined,
    usageReport: undefined,
    usageByYearMonth: {},
};

export const usageDataSlice = createSlice({
    name: 'usageData',
    initialState,
    reducers: {
        setUsageDataSubscription(
            state: UsageDataStore,
            action: PayloadAction<UsageDataSubscriptionDto>
        ) {
            state.usageDataSubscription = action.payload;
        },
        setUsageReport(
            state: UsageDataStore,
            action: PayloadAction<UsageReport>
        ) {
            state.usageReport = action.payload;
        },
        setUsagePeriods(
            state: UsageDataStore,
            action: PayloadAction<UsagePeriodsDto>
        ) {
            state.usageByYearMonth = groupPeriodsByYearMonth(
                action.payload.available_periods
            );
        },
    },
});

function groupPeriodsByYearMonth(periods: UsagePeriod[]): UsageByYearMonth {
    if (!periods) {
        return {};
    }
    return periods.reduce((acc, it) => {
        if (it.year in acc) {
            acc[it.year][it.month] = it;
        } else {
            acc[it.year] = { [it.month]: it };
        }
        return acc;
    }, {});
}

export function LoadUsageDataSubscription() {
    return async (dispatch: PbDispatch) => {
        try {
            const result =
                await processRequestForAccountingAreaWithReplacement<UsageDataSubscriptionDto>(
                    dispatch,
                    `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/usage_data_subscription/`,
                    { method: 'GET' },
                    PbRequest.GET_USAGE_DATA_SUBSCRIPTION
                );
            dispatch(usageDataSlice.actions.setUsageDataSubscription(result));
        } catch (e) {
            console.log('failed to get usage data subscription', e);
        }
    };
}

export function StoreUsageDataSubscription(
    usageDataSubscription: UsageDataSubscriptionDto
) {
    return async (dispatch: PbDispatch) => {
        try {
            await processRequestForAccountingAreaWithReplacement<UsageDataSubscriptionDto>(
                dispatch,
                `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/usage_data_subscription/`,
                {
                    method: 'PUT',
                    body: JSON.stringify(usageDataSubscription),
                },
                PbRequest.PUT_USAGE_DATA_SUBSCRIPTION
            );
        } catch (e) {
            console.log('failed to put usage data subscription', e);
        }
    };
}

export function LoadUsagePeriods() {
    return async (dispatch: PbDispatch) => {
        try {
            const result =
                await processRequestForAccountingAreaWithReplacement<UsagePeriodsDto>(
                    dispatch,
                    `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/usage_periods/`,
                    { method: 'GET' },
                    PbRequest.GET_USAGE_PERIODS
                );
            dispatch(usageDataSlice.actions.setUsagePeriods(result));
        } catch (e) {
            console.log('failed to get usage periods', e);
        }
    };
}

export function LoadUsageReport(contractPeriod: string) {
    return async (dispatch: PbDispatch) => {
        try {
            const result =
                await processRequestForAccountingAreaWithReplacement<UsageReport>(
                    dispatch,
                    `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/usage_periods/${contractPeriod}`,
                    { method: 'GET' },
                    PbRequest.GET_USAGE_REPORT
                );
            dispatch(usageDataSlice.actions.setUsageReport(result));
        } catch (e) {
            console.log('failed to get usage report', e);
        }
    };
}

export default usageDataSlice.reducer;
